import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { Search, Plus, Trash2, Star, ArrowRight, PackageSearch, BadgeCheck } from "lucide-react";

export default function EstimatePage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [origins, setOrigins] = useState([]);
  const [dests, setDests] = useState([]);
  const [oc, setOc] = useState(params.get("oc") || "");
  const [dc, setDc] = useState(params.get("dc") || "");
  const [mode, setMode] = useState(["air", "sea"].includes(params.get("mode")) ? params.get("mode") : "sea");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [cart, setCart] = useState([]);
  const [weightKg, setWeightKg] = useState("");
  const [offers, setOffers] = useState(null);
  const [loading, setLoading] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [custom, setCustom] = useState({ name: "", qty: 1, weight_est: "", length_cm: "", width_cm: "", height_cm: "", description: "", fragile: false, condition: "new", value: "" });

  useEffect(() => {
    api.get("/countries?kind=origin").then(({ data }) => setOrigins(data));
    api.get("/countries?kind=destination").then(({ data }) => setDests(data));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      if (q.trim().length >= 2) api.get(`/catalog/search?q=${encodeURIComponent(q)}&mode=${mode === "all" ? "" : mode}`).then(({ data }) => setResults(data));
      else setResults([]);
    }, 250);
    return () => clearTimeout(t);
  }, [q, mode]);

  // Changement de mode sécurisé (5A-2B) : estimation invalidée, articles incompatibles
  // retirés avec message clair — jamais de prix de l'ancien mode conservé silencieusement.
  const changeMode = (v) => {
    setMode(v); setOffers(null); setResults([]);
    setCart((c) => {
      const keep = c.filter((x) => x.custom || !x.allowed_modes || x.allowed_modes === "both" || x.allowed_modes === v);
      const removed = c.filter((x) => !keep.includes(x)).map((x) => x.name);
      if (removed.length) {
        toast.error(`${removed.join(", ")} : ${t("est.modeIncompatible")}`);
      }
      return keep;
    });
  };

  const addArticle = (a) => {
    setCart((c) => {
      const ex = c.find((x) => x.article_id === a.id);
      if (ex) return c.map((x) => (x.article_id === a.id ? { ...x, qty: x.qty + 1 } : x));
      return [...c, { article_id: a.id, name: a.name_fr, qty: 1, flags: a.flags || [], allowed_modes: a.allowed_modes || "both" }];
    });
    setQ(""); setResults([]); setOffers(null);
  };

  const addCustom = async () => {
    if (!custom.name.trim()) { toast.error(t("est.nameReq")); return; }
    const payload = { ...custom, qty: Number(custom.qty) || 1, weight_est: Number(custom.weight_est) || 0, length_cm: Number(custom.length_cm) || 0, width_cm: Number(custom.width_cm) || 0, height_cm: Number(custom.height_cm) || 0, value: Number(custom.value) || 0 };
    if (user?.role === "customer") { try { await api.post("/catalog/unlisted", payload); } catch (e) { /* non bloquant */ } }
    setCart((c) => [...c, { custom: payload, name: `${custom.name} (non répertorié)`, qty: payload.qty, unlisted: true }]);
    setCustomOpen(false);
    setCustom({ name: "", qty: 1, weight_est: "", length_cm: "", width_cm: "", height_cm: "", description: "", fragile: false, condition: "new", value: "" });
    setOffers(null);
    toast.success(t("est.addedUnlisted"));
  };

  const compare = async () => {
    if (!oc || !dc) { toast.error(t("est.chooseOD")); return; }
    if (cart.length === 0 && !Number(weightKg)) { toast.error(t("est.addOne")); return; }
    setLoading(true);
    try {
      const items = cart.map((x) => (x.custom ? { custom: x.custom, qty: x.qty } : { article_id: x.article_id, qty: x.qty }));
      const { data } = await api.post("/catalog/estimate", { origin_country: oc, dest_country: dc, mode, items, weight_kg: Number(weightKg) || 0 });
      setOffers(data);
      if (data.length === 0) toast.info(t("est.noOffer"));
    } finally { setLoading(false); }
  };

  const choose = async (o) => {
    if (!user) { navigate("/login"); return; }
    if (user.role !== "customer") { toast.error(t("est.custOnly")); return; }
    try {
      const items = cart.map((x) => (x.custom ? { custom: x.custom, qty: x.qty } : { article_id: x.article_id, qty: x.qty }));
      const { data } = await api.post("/catalog/book", { partner_id: o.partner.id, route_id: o.route.id, mode: o.route.mode, dest_city: o.route.dest_city, items, weight_kg: Number(weightKg) || 0 });
      toast.success(t("est.created").replace("{ref}", data.reference));
      navigate(`/shipments/${data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || t("est.bookErr")); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10">
        <h1 className="font-heading font-extrabold text-3xl tracking-tight">{t("est.title")}</h1>
        <p className="mt-1 text-muted-foreground text-sm">{t("est.subtitle")}</p>

        {/* Corridor */}
        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          <Select value={oc} onValueChange={(v) => { setOc(v); setOffers(null); }}>
            <SelectTrigger data-testid="est-origin" className="rounded-sm"><SelectValue placeholder={t("est.originPh")} /></SelectTrigger>
            <SelectContent>{origins.map((c) => <SelectItem key={c.id} value={c.name}>{c.flag} {c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={dc} onValueChange={(v) => { setDc(v); setOffers(null); }}>
            <SelectTrigger data-testid="est-dest" className="rounded-sm"><SelectValue placeholder={t("est.destPh")} /></SelectTrigger>
            <SelectContent>{dests.map((c) => <SelectItem key={c.id} value={c.name}>{c.flag} {c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={mode} onValueChange={changeMode}>
            <SelectTrigger data-testid="est-mode" className="rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="sea">{t("search.sea")}</SelectItem><SelectItem value="air">{t("search.air")}</SelectItem></SelectContent>
          </Select>
        </div>

        {/* Recherche article */}
        <div className="mt-5 relative">
          <div className="flex items-center gap-2 border border-border bg-card px-3">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input data-testid="est-search" className="border-0 focus-visible:ring-0 rounded-none" placeholder={t("est.searchPh")} value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          {results.length > 0 && (
            <div className="absolute z-20 mt-1 w-full border border-border bg-card shadow-lg max-h-72 overflow-y-auto" data-testid="est-results">
              {results.map((a) => (
                <button key={a.id} data-testid={`est-add-${a.id}`} className="w-full text-left px-3 py-2 text-sm hover:bg-secondary flex items-center justify-between" onClick={() => addArticle(a)}>
                  <span>{a.name_fr}</span>
                  <span className="flex gap-1">
                    {a.quote_required && <Badge className="rounded-sm bg-amber-100 text-amber-700 text-[10px]">{t("common.onQuote")}</Badge>}
                    {(a.flags || []).includes("battery") && <Badge className="rounded-sm bg-red-100 text-red-700 text-[10px]">{t("est.battery")}</Badge>}
                    <Plus className="h-4 w-4 text-accent" />
                  </span>
                </button>
              ))}
            </div>
          )}
          {q.trim().length >= 2 && results.length === 0 && (
            <div className="absolute z-20 mt-1 w-full border border-border bg-card shadow-lg p-3 text-sm">
              <p className="text-muted-foreground">{t("goods.notInCatalog")}</p>
              <Button size="sm" data-testid="est-add-custom-btn" className="mt-2 rounded-sm bg-accent text-white gap-1" onClick={() => { setCustom((c) => ({ ...c, name: q })); setCustomOpen(true); }}><Plus className="h-4 w-4" /> {t("goods.addCustom")}</Button>
            </div>
          )}
        </div>

        {/* Panier */}
        <div className="mt-5 border border-border bg-card p-4">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-bold flex items-center gap-2"><PackageSearch className="h-4 w-4 text-accent" /> {t("est.cartTitle")}</h2>
            <Button size="sm" variant="outline" className="rounded-sm gap-1" data-testid="est-custom-open" onClick={() => setCustomOpen(true)}><Plus className="h-4 w-4" /> {t("goods.addCustom")}</Button>
          </div>
          <div className="mt-3 space-y-2" data-testid="est-cart">
            {cart.map((x, i) => (
              <div key={i} className="flex items-center justify-between gap-2 border border-border p-2 text-sm">
                <span className="font-medium">{x.name} {(x.flags || []).includes("battery") && <span className="text-[11px] text-red-600">{t("est.batteryWarn")}</span>}</span>
                <span className="flex items-center gap-2">
                  <Input data-testid={`est-qty-${i}`} type="number" min="1" className="rounded-sm h-8 w-16" value={x.qty}
                    onChange={(e) => { const v = Math.max(1, Number(e.target.value) || 1); setCart((c) => c.map((y, j) => (j === i ? { ...y, qty: v } : y))); setOffers(null); }} />
                  <button data-testid={`est-remove-${i}`} onClick={() => { setCart((c) => c.filter((_, j) => j !== i)); setOffers(null); }}><Trash2 className="h-4 w-4 text-red-500" /></button>
                </span>
              </div>
            ))}
            {cart.length === 0 && <p className="text-xs text-muted-foreground">{t("est.cartEmpty")}</p>}
          </div>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <div><Label className="text-xs">{t("est.smallParcel")}</Label><Input data-testid="est-weight" type="number" className="rounded-sm w-36" placeholder="ex : 15" value={weightKg} onChange={(e) => { setWeightKg(e.target.value); setOffers(null); }} /></div>
            <Button data-testid="est-compare-btn" className="rounded-sm bg-accent hover:bg-accent/90 text-white" onClick={compare} disabled={loading}>{loading ? t("est.calc") : t("est.cta")}</Button>
          </div>
        </div>

        {/* Offres */}
        {offers && (
          <div className="mt-6 space-y-4" data-testid="est-offers">
            {offers.map((o) => (
              <div key={o.route.id} data-testid={`offer-${o.partner.id}`} className="border border-border bg-card p-5 hover:border-accent transition-colors">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-heading font-bold text-lg">{o.partner.company_name}</span>
                  {o.partner.verified && <Badge className="bg-emerald-600 text-white rounded-sm gap-1"><BadgeCheck className="h-3.5 w-3.5" /> {t("est.verified")}</Badge>}
                  <span className="text-xs flex items-center gap-1 text-muted-foreground"><Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" /> {o.partner.rating}/5 · {o.route.delay_days} {t("est.days")} · {o.route.mode === "air" ? t("search.air") : t("search.sea")}</span>
                  {o.via_hub && <Badge data-testid={`offer-viahub-${o.partner.id}`} className="rounded-sm bg-primary text-white">{t("est.viaHub")}</Badge>}
                </div>
                <div className="mt-3 space-y-1 text-sm">
                  {o.lines.map((l, i) => (
                    <div key={i} className="flex items-center justify-between border-b border-dashed border-border pb-1">
                      <span>{l.qty} × {l.name}{l.notice && <span className="text-[11px] text-red-600 ml-1">· {l.notice}</span>}</span>
                      <span className="font-semibold">
                        {l.status === "priced" && `${l.unit_price} × ${l.qty} = ${l.line_total} ${l.currency}`}
                        {l.status === "quote" && <Badge className="rounded-sm bg-amber-100 text-amber-700">{t("common.onQuote")}</Badge>}
                        {l.status === "approval" && <Badge className="rounded-sm bg-orange-100 text-orange-700">{t("est.approval")}{l.line_total ? ` · ${l.line_total} ${l.currency}` : ""}</Badge>}
                      </span>
                    </div>
                  ))}
                  {o.weight_line && (
                    <div className="flex items-center justify-between border-b border-dashed border-border pb-1">
                      <span>{o.weight_line.name}</span>
                      <span className="font-semibold">{o.weight_line.status === "priced" ? `${o.weight_line.line_total} ${o.weight_line.currency}` : <Badge className="rounded-sm bg-amber-100 text-amber-700">{t("common.onQuote")}</Badge>}</span>
                    </div>
                  )}
                  {o.fees > 0 && <div className="flex items-center justify-between text-muted-foreground"><span>{t("est.destFees")}</span><span>{o.fees} {o.currency}</span></div>}
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-xs uppercase text-muted-foreground">{t("goods.estimatePrefix").replace(" :","").replace(":","")}{o.quote_needed > 0 ? ` (+ ${o.quote_needed} ${t("goods.onQuoteShort")})` : ""}</div>
                    <div className="font-heading font-extrabold text-2xl text-accent" data-testid={`offer-total-${o.partner.id}`}>{o.total} {o.currency}</div>
                    <p className="text-[11px] text-muted-foreground">{t("sd.indicative")}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button data-testid={`offer-choose-${o.partner.id}`} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1" onClick={() => choose(o)}>{t("est.book")} <ArrowRight className="h-4 w-4" /></Button>
                  </div>
                </div>
              </div>
            ))}
            {offers.length === 0 && <div className="border border-dashed p-10 text-center text-muted-foreground">{t("est.noOffer")}</div>}
          </div>
        )}
      </div>

      {/* Dialog article personnalisé */}
      <Dialog open={customOpen} onOpenChange={setCustomOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("goods.customTitle")}</DialogTitle><DialogDescription>{t("est.customDesc")}</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Nom de l&rsquo;article</Label><Input data-testid="custom-name" className="rounded-sm" value={custom.name} onChange={(e) => setCustom({ ...custom, name: e.target.value })} /></div>
            <div><Label>{t("goods.customQty")}</Label><Input data-testid="custom-qty" type="number" className="rounded-sm" value={custom.qty} onChange={(e) => setCustom({ ...custom, qty: e.target.value })} /></div>
            <div><Label>{t("goods.customWeight")}</Label><Input data-testid="custom-weight" type="number" className="rounded-sm" value={custom.weight_est} onChange={(e) => setCustom({ ...custom, weight_est: e.target.value })} /></div>
            <div><Label>L × l × H (cm)</Label>
              <div className="flex gap-1">
                <Input type="number" className="rounded-sm" placeholder="L" value={custom.length_cm} onChange={(e) => setCustom({ ...custom, length_cm: e.target.value })} />
                <Input type="number" className="rounded-sm" placeholder="l" value={custom.width_cm} onChange={(e) => setCustom({ ...custom, width_cm: e.target.value })} />
                <Input type="number" className="rounded-sm" placeholder="H" value={custom.height_cm} onChange={(e) => setCustom({ ...custom, height_cm: e.target.value })} />
              </div>
            </div>
            <div><Label>État</Label>
              <Select value={custom.condition} onValueChange={(v) => setCustom({ ...custom, condition: v })}>
                <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="new">Neuf</SelectItem><SelectItem value="used">Occasion</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Valeur (facultatif)</Label><Input type="number" className="rounded-sm" value={custom.value} onChange={(e) => setCustom({ ...custom, value: e.target.value })} /></div>
            <div className="flex items-center gap-2 pt-5"><input id="cfr" type="checkbox" checked={custom.fragile} onChange={(e) => setCustom({ ...custom, fragile: e.target.checked })} /><Label htmlFor="cfr">Fragile</Label></div>
            <div className="col-span-2"><Label>Description</Label><Textarea data-testid="custom-desc" className="rounded-sm" value={custom.description} onChange={(e) => setCustom({ ...custom, description: e.target.value })} /></div>
          </div>
          <DialogFooter><Button data-testid="custom-submit" className="rounded-sm bg-accent text-white" onClick={addCustom}>{t("goods.customSubmit")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
